import React, { memo, useCallback, useMemo } from 'react'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { EmptyInsuranceProducts } from 'src/components/insurance-product/grid/EmptyInsuranceProducts'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  INSURANCE_PRODUCTS_GRID_COLUMNS_STORAGE_KEY,
  insuranceProductPageSizeOptions,
} from 'src/constants/insurance-product/constants'
import { InsuranceProductsTableFields } from 'src/constants/insurance-product/enums'
import { buildInsuranceProductColumns } from 'src/constants/insurance-product/insuranceProductGrid'
import type { InsuranceProduct } from 'src/interfaces/insuranceProduct'
import { getInsuranceProducts, getInsuranceProductsLoading } from 'src/selectors/insuranceProduct'
import { filterInsuranceProducts } from 'src/utils/insuranceProductList'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  searchQuery: string
  canManage: boolean
  onEditInsuranceProduct(insuranceProduct: InsuranceProduct): void
  onDeleteInsuranceProduct(insuranceProduct: InsuranceProduct): void
}

const getRowId = (row: InsuranceProduct) => {
  return row.id
}

// Name identifies the row and the actions column has no meaning when hidden; both also carry
// `hideable: false`, which is what removes them from the manage-columns panel. This guard
// additionally stops a programmatic visibility-model change from persisting a hide.
const UNHIDEABLE_COLUMNS: Array<string> = [
  InsuranceProductsTableFields.name,
  InsuranceProductsTableFields.actions,
]

const InsuranceProductsTableComponent = ({
  searchQuery,
  canManage,
  onEditInsuranceProduct,
  onDeleteInsuranceProduct,
}: Props) => {
  const insuranceProducts = useSelector(getInsuranceProducts)
  const isLoading = useSelector(getInsuranceProductsLoading)

  const { paginationModel, setPaginationModel } = useGridPagination(insuranceProductPageSizeOptions)

  // A fresh array each render would reset the grid's column state, so the factory call is
  // memoized — it depends only on the two stable handlers and the permission flag, which is fixed
  // for the session, so a delete no longer rebuilds it.
  const insuranceProductColumns = useMemo(() => {
    return buildInsuranceProductColumns(onEditInsuranceProduct, onDeleteInsuranceProduct, canManage)
  }, [onEditInsuranceProduct, onDeleteInsuranceProduct, canManage])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    insuranceProductColumns,
    INSURANCE_PRODUCTS_GRID_COLUMNS_STORAGE_KEY,
  )

  // The client holds the whole catalog (the endpoint takes no search param), so filtering is here.
  const visibleInsuranceProducts = useMemo(() => {
    return filterInsuranceProducts(insuranceProducts, searchQuery)
  }, [insuranceProducts, searchQuery])

  const columnVisibilityModel = useMemo(() => {
    return insuranceProductColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [insuranceProductColumns, includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility model.
  // Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      insuranceProductColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [insuranceProductColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  const hasSearchQuery = searchQuery.trim() !== ''

  // `GridTable` renders EmptyElement with no props, so the search-aware copy is bound here.
  const EmptyElement = useMemo(() => {
    const BoundEmptyInsuranceProducts = () => {
      return <EmptyInsuranceProducts hasSearchQuery={hasSearchQuery} />
    }

    return BoundEmptyInsuranceProducts
  }, [hasSearchQuery])

  // Pinning is intentionally left uncontrolled, for the reason documented on CustomersTable:
  // `useGridColumns`'s persisted list has no left/right distinction.
  return (
    <GridTable<InsuranceProduct>
      rows={visibleInsuranceProducts}
      columns={insuranceProductColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={insuranceProductPageSizeOptions}
      initialState={{
        // The backend list query has NO `ORDER BY` (para-node InsuranceProductRepository.findAll
        // is a bare getMany), so this sort model is the only thing making the order stable.
        sorting: { sortModel: [{ field: InsuranceProductsTableFields.name, sort: 'asc' }] },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyElement}
      tableDataTestId="insurance-products-list-table"
      rowDataTestIdPrefix="insurance-products-list"
    />
  )
}

export const InsuranceProductsTable = memo(InsuranceProductsTableComponent)
